import { ApiConfig } from '../../config/api';
import { JWTManager } from '../../utils/jwt';
import { authInterceptor } from '../authInterceptor';

export interface ApiResponse<T = any> {
  data?: T;
  success: boolean;
  error?: string;
  status?: number;
}

export interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean;
  customHeaders?: Record<string, string>;
}

export abstract class BaseApiManager {
  protected baseUrl: string;
  private tokenKey: string = 'nuroleap_token';
  private requestTimeout: number = 30000;

  protected constructor() {
    this.baseUrl = ApiConfig.baseUrl;
  }

  protected async request<T>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const { skipAuth = false, customHeaders = {}, ...fetchOptions } = options;
    
    try {
      const token = skipAuth ? null : this.getToken();
      const headers = this.buildHeaders(token, customHeaders, fetchOptions.headers);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);
      
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle authentication errors from the server
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          message: `HTTP ${response.status}: ${response.statusText}` 
        }));
        
        console.error(`API request failed: ${response.status} for ${endpoint}`);
        
        // Check if this is an authentication error and handle it
        if (response.status === 401 || response.status === 403) {
          console.warn(`API request failed with auth error: ${response.status} for ${endpoint}`);
          // Temporarily disable authInterceptor to prevent loops
          // authInterceptor.handleAuthError(response.status, errorData.message);
          return {
            success: false,
            error: 'Authentication failed',
            status: response.status
          };
        }
        
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const text = await response.text();
      const data = text ? JSON.parse(text) : null;
      return { data, success: true, status: response.status };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Connection failed';
      console.error(`API request failed for ${endpoint}:`, errorMessage);
      return {
        success: false,
        error: errorMessage,
        status: error instanceof Error && error.name === 'AbortError' ? 408 : 500
      };
    }
  }

  protected getToken(): string | null {
    return JWTManager.getToken();
  }

  private buildHeaders(
    token?: string | null, 
    customHeaders?: Record<string, string>,
    fetchHeaders?: HeadersInit
  ): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    if (fetchHeaders) {
      if (Array.isArray(fetchHeaders)) {
        fetchHeaders.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else if (typeof fetchHeaders === 'object') {
        Object.assign(headers, fetchHeaders);
      }
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  public setToken(token: string): void {
    JWTManager.setToken(token);
  }

  public removeToken(): void {
    JWTManager.removeToken();
  }

  public isTokenValid(): boolean {
    return JWTManager.isTokenValid();
  }

  public getUserId(): number | null {
    return JWTManager.getUserId();
  }

  public getUsername(): string | null {
    return JWTManager.getUsername();
  }

  public getUserEmail(): string | null {
    return JWTManager.getUserEmail();
  }

  public setRequestTimeout(timeout: number): void {
    this.requestTimeout = timeout;
  }

  public getBaseUrl(): string {
    return this.baseUrl;
  }
}
